/**
 * The manager's view (docs/refactor/40), a tab of Acompanhar › Relatórios: for one specification, or for one day,
 * which agent did the work, in which session, on which files, and what it cost. The reason to pay per seat, so
 * outside the Team plan it says what it is and where to get it, never half of it.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bot, FileCode2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { Badge, ErrorBanner, Panel, Segmented, Skeleton } from './primitives'
import type { Deliveries as DeliveriesData, DeliverySession } from '../lib/types'

type Mode = 'spec' | 'day'

/** Today where the person is, not in UTC: at 22h in Brasília UTC is already tomorrow. */
const today = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

export function Deliveries() {
  const { t, locale } = useI18n()
  const [mode, setMode] = useState<Mode>('spec')
  const [specId, setSpecId] = useState('')
  const [day, setDay] = useState(today)
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000 })
  const entitled = plan?.entitlements.managerView === true
  const { data: specs } = useQuery({ queryKey: ['specs', 'deliveries'], queryFn: () => api.specs.list(), enabled: entitled })
  const chosenSpec = specId || specs?.specs[0]?.id || ''

  const { data, isLoading, error } = useQuery({
    queryKey: ['deliveries', mode, mode === 'spec' ? chosenSpec : day],
    queryFn: () => api.deliveries.get(mode === 'spec' ? { specificationId: chosenSpec } : { day, offsetMinutes: -new Date().getTimezoneOffset() }),
    enabled: entitled && (mode === 'day' || Boolean(chosenSpec)),
  })

  if (plan && !entitled) {
    return (
      <Panel title={t('deliveries.title')}>
        <div className="stack">
          <p className="small">{t('deliveries.what')}</p>
          <p className="small muted">{t('deliveries.teamOnly')}</p>
          <div><Link className="btn btn--primary btn--sm" to="/settings/billing">{t('plans.upgrade')}</Link></div>
        </div>
      </Panel>
    )
  }

  // The API leaves out fields that are null: an unknown cost arrives as undefined.
  const money = (value: number | null | undefined, accuracy: string) =>
    value == null ? t('deliveries.costUnknown') : `${accuracy === 'Estimated' ? '≈ ' : ''}US$ ${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <Panel title={t('deliveries.title')}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{t('deliveries.what')}</p>
        <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
          <Segmented<Mode> value={mode} options={[{ value: 'spec', label: t('deliveries.bySpec') }, { value: 'day', label: t('deliveries.byDay') }]} onChange={setMode} />
          {mode === 'spec' ? (
            <select className="select select--sm" value={chosenSpec} onChange={(event) => setSpecId(event.target.value)} aria-label={t('deliveries.bySpec')}>
              {(specs?.specs ?? []).map((spec) => (
                <option key={spec.id} value={spec.id}>{spec.key} — {spec.title}</option>
              ))}
            </select>
          ) : (
            <input className="input" type="date" value={day} max={today()} onChange={(event) => setDay(event.target.value)} style={{ width: 160 }} aria-label={t('deliveries.byDay')} />
          )}
        </div>
        {mode === 'spec' && specs && specs.specs.length === 0 && <p className="small muted">{t('deliveries.noSpecs')}</p>}
        {isLoading && <Skeleton height={160} />}
        {error && <ErrorBanner message={error instanceof ApiError && error.code === 'plan_team_only' ? t('deliveries.teamOnly') : t('error.generic')} />}
        {data && <DeliveryTable data={data} money={money} />}
      </div>
    </Panel>
  )
}

function DeliveryTable({ data, money }: { data: DeliveriesData; money: (value: number | null | undefined, accuracy: string) => string }) {
  const { t, locale } = useI18n()
  if (data.sessions.length === 0) return <p className="small muted">{t('deliveries.empty', { days: data.historyDays })}</p>
  return (
    <div className="stack">
      <div className="row row--wrap small" style={{ gap: 'var(--s5)' }}>
        <span><strong>{data.sessions.length}</strong> {t('deliveries.sessions')}</span>
        <span><strong>{data.people}</strong> {t('deliveries.people')}</span>
        <span><strong>{data.filesChanged}</strong> {t('deliveries.files')}</span>
        <span><strong>{money(data.costUsd, data.costAccuracy)}</strong>{data.costUsd != null && <> {t('deliveries.cost')}</>}</span>
      </div>
      <div className="table-wrap">
        <table className="table deliveries">
          <thead>
            <tr>
              <th>{t('deliveries.col.agent')}</th>
              <th>{t('deliveries.col.session')}</th>
              <th>{t('deliveries.col.files')}</th>
              <th style={{ textAlign: 'right' }}>{t('deliveries.col.cost')}</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.map((s) => (
              <tr key={s.workSessionId}>
                <td>
                  <div className="row row--tight">
                    {s.isAgent && <Bot size={13} aria-label={t('deliveries.agentPersona')} />}
                    <strong>{s.developerName}</strong>
                  </div>
                  <span className="tiny muted">{s.agent}{s.model ? ` · ${s.model}` : ''}</span>
                </td>
                <td>
                  <Link to={`/work/${s.workSessionId}`}>{s.task ? `${s.task.externalId} · ${s.task.title}` : s.repository ?? t('work.untitled')}</Link>
                  <div className="tiny muted">
                    {new Date(s.startedAt).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })}
                    {s.spec && <> · <Link to={`/project/specs?spec=${s.spec.id}`}>{s.spec.key}</Link></>}
                    {' · '}<Badge tone="neutral">{t(`sessionStatus.${s.status}`)}</Badge>
                  </div>
                </td>
                <td><FileList session={s} /></td>
                <td style={{ textAlign: 'right' }} className="mono">{money(s.costUsd, s.costAccuracy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FileList({ session }: { session: DeliverySession }) {
  const { t } = useI18n()
  const [all, setAll] = useState(false)
  const files = session.files ?? []
  if (!session.filesChanged && files.length === 0) return <span className="tiny muted">—</span>
  const shown = all ? files : files.slice(0, 3)
  return (
    <div className="stack stack--tight">
      <span className="tiny"><FileCode2 size={11} /> {t('deliveries.filesCount', { count: session.filesChanged, added: session.linesAdded, deleted: session.linesDeleted })}</span>
      {shown.map((file) => <span key={file} className="tiny mono muted deliveries__file">{file}</span>)}
      {!all && files.length > 3 && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAll(true)}>{t('deliveries.moreFiles', { count: files.length - 3 })}</button>
      )}
    </div>
  )
}
